import { useEffect, useState } from 'react';
import { useGetPaginateAssets } from '../../../../../api-http/assets';
import {
  AssetFileType,
  AssetsResponse,
} from '../../../../../types/assets.types';
import { OrderEnum } from '../../../../../types/pagination.types';
import { useSnack } from '../../../../../util/useSnack';

const PAGE_SIZE = 20;

// The API matches the search as a regular expression and the hook puts it
// in the query string unencoded: search for the text as typed.
const toSearchParam = (search: string) =>
  encodeURIComponent(search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));

// The organization's images or videos, newest first, a page at a time.
export const useMediaLibrary = (
  fileType: AssetFileType.Image | AssetFileType.Video,
) => {
  const { showErrSnack } = useSnack();
  // Changed together, so a page is never requested for an old search.
  const [query, setQuery] = useState({ search: '', page: 1 });
  const [items, setItems] = useState<AssetsResponse[]>([]);
  const { data, error, isFetching, refetch } = useGetPaginateAssets({
    limit: PAGE_SIZE,
    page: query.page,
    sortby: 'createdAt',
    order: OrderEnum.desc,
    fileType,
    search: toSearchParam(query.search),
  });

  useEffect(() => {
    if (error) showErrSnack(error);
  }, [error]);

  useEffect(() => {
    if (!data) return;
    setItems((previous) => {
      if (query.page === 1) return data.data;
      // A page can be fetched again (e.g. when the window regains focus).
      const shown = new Set(previous.map((item) => item._id));
      return [...previous, ...data.data.filter((item) => !shown.has(item._id))];
    });
  }, [data]);

  const setSearch = (search: string) => {
    if (search === query.search) return;
    setItems([]);
    setQuery({ search, page: 1 });
  };

  // After an upload: the new file is first.
  const refresh = () => {
    if (query.page === 1) refetch();
    else setQuery({ ...query, page: 1 });
  };

  return {
    items,
    search: query.search,
    isLoading: isFetching && !items.length,
    isLoadingMore: isFetching && items.length > 0,
    hasMore: !!data && items.length < data.pagination.total,
    setSearch,
    loadMore: () => setQuery({ ...query, page: query.page + 1 }),
    refresh,
  };
};

export type MediaLibrary = ReturnType<typeof useMediaLibrary>;
